import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import { useAuth } from './context/AuthContext';
import Header from './components/Header';
import Hero from './components/Hero';
import SimulationBrowser from './components/SimulationBrowser';
import GameEmbed from './components/GameEmbed';
import AuthModal from './components/AuthModal';
import StoryMode from './components/StoryMode';
import SparcStoryPlayer from './components/SparcStoryPlayer';
import NewtonStoryPlayer from './components/NewtonStoryPlayer';
import PrivacyPolicy from './components/PrivacyPolicy';
import TermsOfService from './components/TermsOfService';
import CookiePolicy from './components/CookiePolicy';
import TeacherDashboard from './components/TeacherDashboard';
import ClassDetails from './components/ClassDetails';
import TeachingPublic from './components/TeachingPublic';
import Initiatives from './components/Initiatives';
import Dashboard from './components/Dashboard';
import AccountCenter from './components/AccountCenter';
import AdminCenter from './components/AdminCenter';
import ResearchHub from './components/ResearchHub';
import './App.css';

const AdminGate = ({ children }) => {
  const { isAuthenticated, user } = useAuth();
  const isAdmin = isAuthenticated && user && (
    user.role === 'org_admin' ||
    user.role === 'platform_admin'
  );

  if (!isAdmin) {
    return <div className="container"><h2>Admin access required.</h2></div>;
  }

  return children;
};

const PlatformAdminGate = ({ children }) => {
  const { isAuthenticated, user } = useAuth();
  const ok = isAuthenticated && user && user.role === 'platform_admin';
  if (!ok) {
    return <div className="container"><h2>Platform admin access required.</h2></div>;
  }
  return children;
};

const TeachingGate = ({ onLoginClick, children }) => {
  const { isAuthenticated, user } = useAuth();
  const isTeacher = isAuthenticated && user && (
    user.role === 'teacher' ||
    user.role === 'org_admin' ||
    user.role === 'platform_admin'
  );

  if (!isTeacher) {
    return <TeachingPublic onLoginClick={onLoginClick} />;
  }

  return children;
};

function AppContent() {
  const [currentPage, setCurrentPage] = useState('simulations');
  const [searchQuery, setSearchQuery] = useState('');
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [authMode, setAuthMode] = useState('login');
  const [authRedirect, setAuthRedirect] = useState('');
  const location = useLocation();
  const isStoryRoute = location.pathname.startsWith('/story/');

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const auth = params.get('auth');
    const returnUrl = params.get('return');
    if (returnUrl) {
      setAuthRedirect(returnUrl);
    }
    if (auth === 'login' || auth === 'register') {
      setAuthMode(auth);
      setShowAuthModal(true);
      const cleanUrl = window.location.pathname;
      window.history.replaceState({}, document.title, cleanUrl);
    }
  }, []);

  return (
        <div className={`App${isStoryRoute ? ' story-app' : ''}`}>
          {!isStoryRoute && (
            <Header 
            currentPage={currentPage} 
            setCurrentPage={setCurrentPage}
            setSearchQuery={setSearchQuery}
            onLoginClick={() => { setAuthMode('login'); setShowAuthModal(true); }}
          />
          )}
          <Routes>
            <Route path="/" element={
              <>
                <Hero searchQuery={searchQuery} setSearchQuery={setSearchQuery} />
                <StoryMode />
                <SimulationBrowser searchQuery={searchQuery} />
              </>
            } />
            <Route path="/story/sparc" element={<SparcStoryPlayer />} />
            <Route path="/story/newton" element={<NewtonStoryPlayer />} />
            <Route path="/game/:gameId" element={<GameEmbed />} />
            <Route path="/studio" element={<div className="container"><h1>Studio</h1></div>} />
            <Route
              path="/teaching"
              element={
                <TeachingGate onLoginClick={() => setShowAuthModal(true)}>
                  <TeacherDashboard />
                </TeachingGate>
              }
            />
            <Route
              path="/teaching/classes/:classId"
              element={
                <TeachingGate onLoginClick={() => setShowAuthModal(true)}>
                  <ClassDetails />
                </TeachingGate>
              }
            />
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/account" element={<AccountCenter />} />
            <Route
              path="/admin"
              element={
                <AdminGate>
                  <AdminCenter />
                </AdminGate>
              }
            />

            <Route
              path="/admin/users"
              element={
                <AdminGate>
                  <AdminCenter defaultSection="users" />
                </AdminGate>
              }
            />

            <Route
              path="/admin/players"
              element={
                <AdminGate>
                  <AdminCenter defaultSection="players" />
                </AdminGate>
              }
            />

            <Route
              path="/admin/modules"
              element={
                <PlatformAdminGate>
                  <AdminCenter defaultSection="modules" />
                </PlatformAdminGate>
              }
            />

            <Route
              path="/admin/templates"
              element={
                <PlatformAdminGate>
                  <AdminCenter defaultSection="templates" />
                </PlatformAdminGate>
              }
            />

            <Route
              path="/admin/organizations"
              element={
                <PlatformAdminGate>
                  <AdminCenter defaultSection="orgs" />
                </PlatformAdminGate>
              }
            />

            <Route
              path="/admin/telemetry"
              element={
                <PlatformAdminGate>
                  <AdminCenter defaultSection="telemetry" />
                </PlatformAdminGate>
              }
            />
            <Route path="/research" element={<ResearchHub />} />
            <Route path="/initiatives" element={<Initiatives />} />
            <Route path="/privacy" element={<PrivacyPolicy />} />
            <Route path="/terms" element={<TermsOfService />} />
            <Route path="/cookie-policy" element={<CookiePolicy />} />
          </Routes>

          {!isStoryRoute && (
            <footer className="site-footer">
              © 2026 AGAII Organization. All rights reserved.
            </footer>
          )}

          <AuthModal 
            isOpen={showAuthModal} 
            onClose={() => setShowAuthModal(false)} 
            defaultMode={authMode}
            redirectUrl={authRedirect}
          />
        </div>
  );
}

function App() {
  return (
    <AuthProvider>
      <Router>
        <AppContent />
      </Router>
    </AuthProvider>
  );
}

export default App;
